import { Injectable, NgZone } from '@angular/core';
import { fabric } from 'fabric';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { BoardFolder, Frame } from '../../../../models/session';
import { PDFBoardStrategy } from '../../../../models/upload';
import { PanelView } from '../../../panel/panel.component';
import { RealtimeSpaceService } from '../../../../services/realtime-space.service';
import { SpaceBoardFoldersService } from '../../../../services/space-board-folders.service';
import { SessionsVptService } from '../../../../services/sessions-vpt.service';
import { SessionSharedDataService } from '../../../../services/session-shared-data.service';
import { SpaceRepository } from '../../../../state/space.repository';
import { SpaceBoardsService } from '../../../../services/space-boards.service';
import { BoardItemsManager } from '../../items-canvas/board-items/board-items-manager';
import { PDFImagesConfigurations } from '../../items-canvas/board-items/board-images-pdf-configurations';
import { PlaceFormulaSnackbarComponent } from '../../../../ui/place-formula-snackbar/place-formula-snackbar.component';
import { ItemModel } from '../../../../models/itemModel';
import { DeviceAndBrowserDetectorService } from '../../../../services/device-and-browser-detector.service';
import { ConversionResponse, PdfManagerService } from './pdf-manager.service';
import { RENDERED_PDF_PAGES_SCALE } from './pdf-page-to-fabric.service';

export enum PdfArrangement {
  Columns = 'columns',
  Stacked = 'stacked',
}

interface BulkPdfInsertItem {
  contentId: string;
  selectedIndices: number[];
  pdfName: string;
}

interface BulkInsertOptions {
  items: BulkPdfInsertItem[];
  strategy: PDFBoardStrategy;
  arrangement?: PdfArrangement;
  boardId?: string;
  renderInNearestFreeSpace?: boolean;
  uploadAsSeparateImages?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BoardPdfFactoryService {
  readonly GAP_BETWEEN_PDFS = 24;

  constructor(
    private realtimeSpaceService: RealtimeSpaceService,
    private spaceBoardFoldersService: SpaceBoardFoldersService,
    private sessionsVptService: SessionsVptService,
    private sharedDataService: SessionSharedDataService,
    private spaceRepo: SpaceRepository,
    private spaceBoardsService: SpaceBoardsService,
    private pdfManagerService: PdfManagerService,
    private ngZone: NgZone,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private deviceDetector: DeviceAndBrowserDetectorService,
  ) {}

  public async getImagesAndSubmit(
    contentId: string,
    boardStrategy: PDFBoardStrategy,
    selectedIndices: number[],
    pdfName: string,
    uploadAsSeparateImages: boolean = false,
  ) {
    const conversionResponse = await this.pdfManagerService.getConvertedImages(contentId);
    if (!conversionResponse) {
      return;
    }

    switch (boardStrategy) {
      case PDFBoardStrategy.CurrentBoard:
        if (this.deviceDetector.isTouchDevice()) {
          // On touch devices, skip the ghost and place directly in nearest free space
          this.insertPDFItem(
            contentId,
            conversionResponse,
            selectedIndices,
            pdfName,
            undefined,
            true,
            undefined,
            uploadAsSeparateImages,
          );
        } else {
          // Preview Mode for Current Board to choose where to put the PDF
          const firstImageUrl = this.pdfManagerService.getImageUrl(
            selectedIndices[0],
            conversionResponse.imagesPrefix,
            conversionResponse.imagesToken,
          );
          const position = await this.addGhostImageToFollowMouse(firstImageUrl, conversionResponse);

          this.insertPDFItem(
            contentId,
            conversionResponse,
            selectedIndices,
            pdfName,
            undefined,
            false,
            position,
            uploadAsSeparateImages,
          );
        }
        break;
      case PDFBoardStrategy.NewBoard:
        this.insertPDFInNewBoard(
          contentId,
          conversionResponse,
          selectedIndices,
          pdfName,
          uploadAsSeparateImages,
        );
        break;
      case PDFBoardStrategy.OneBoardPerPage:
        this.insertPDFsInBoardFolder(
          contentId,
          conversionResponse,
          selectedIndices,
          pdfName,
          uploadAsSeparateImages,
        );
        break;
    }
  }

  private insertPDFInNewBoard(
    contentId: string,
    conversionResponse: ConversionResponse,
    selectedIndices: number[],
    pdfName: string,
    uploadAsSeparateImages: boolean = false,
  ) {
    const frame = this.createPageFrame(pdfName);
    if (!frame || !this.spaceRepo.activeSpace || !this.spaceRepo.activeSpace.currentRoomUid) {
      return;
    }

    this.realtimeSpaceService.service.addFrame(
      frame,
      this.spaceRepo.activeSpace._id,
      this.spaceRepo.activeSpace.currentRoomUid,
    );

    this.sharedDataService.selectFrameSubject.next(frame.uid);

    // so that inserted object in the middle will be visible
    this.sessionsVptService.resetCanvasToOrigin();

    this.insertPDFItem(
      contentId,
      conversionResponse,
      selectedIndices,
      pdfName,
      frame.uid,
      true,
      undefined,
      uploadAsSeparateImages,
    );
  }

  private insertPDFsInBoardFolder(
    contentId: string,
    conversionResponse: ConversionResponse,
    selectedIndices: number[],
    pdfName: string,
    uploadAsSeparateImages: boolean = false,
  ) {
    // Create board folder
    const folder = new BoardFolder({
      name: pdfName,
      roomUid: this.spaceRepo.activeSpaceCurrentRoomUid ?? '',
    });

    this.realtimeSpaceService.service.addBoardFolder(this.spaceRepo.activeSpaceId ?? '', folder);

    // Create Frames
    const frames: Frame[] = selectedIndices.map((selectedIndex) => {
      const pageNumber = selectedIndex + 1; // Convert 0-based index to 1-based page number
      return this.createPageFrame(`Page ${pageNumber}`, folder.uid);
    });

    // Add them to the board folder
    this.realtimeSpaceService.service.addFrames(
      frames,
      this.spaceRepo.activeSpaceId ?? '',
      this.spaceRepo.activeSpaceCurrentRoomUid ?? '',
    );

    // Open the boards panel
    if (this.sharedDataService.leftPanelView.getValue()?.panelView !== PanelView.tabs) {
      this.sharedDataService.changeLeftPanelView.next(PanelView.tabs);
    }
    // Select the board folder and the first board in it
    this.spaceBoardFoldersService.setActiveSpaceSelectedBoardFolder(folder.uid);
    if (frames.length) {
      this.sharedDataService.selectFrameSubject.next(frames[0].uid);
    }
    // so that inserted object in the middle will be visible
    this.sessionsVptService.resetCanvasToOrigin();

    // Start adding one page per frame
    selectedIndices.forEach((selectedIndex, index) => {
      this.insertPDFItem(
        contentId,
        conversionResponse,
        [selectedIndex],
        pdfName,
        frames[index].uid,
        index === 0,
        undefined,
        uploadAsSeparateImages,
      );
    });
  }

  private insertPDFItem(
    contentId: string,
    conversionResponse: ConversionResponse,
    selectedIndices: number[],
    pdfName: string,
    boardId?: string,
    renderInNearestFreeSpace: boolean = true,
    position?: { top: number; left: number },
    uploadAsSeparateImages: boolean = false,
  ) {
    if (uploadAsSeparateImages) {
      return this.insertPDFAsSeparateImages(
        conversionResponse,
        selectedIndices,
        pdfName,
        boardId,
        renderInNearestFreeSpace,
        position,
      );
    }

    const config: Partial<PDFImagesConfigurations> = {
      contentId,
      imagesSizes: conversionResponse.imagesSizes,
      imagesPrefix: conversionResponse.imagesPrefix,
      imagesToken: conversionResponse.imagesToken,
      numberOfPages: conversionResponse.numberOfPages,
      selectedIndices,
      name: pdfName,
      // Reference back to the original uploaded PDF/office file in `pdf-uploads/`.
      sourceUrl: conversionResponse.sourceUrl,
      top: position?.top,
      left: position?.left,
    };

    // Add board-specific configuration
    if (boardId) {
      config.boardId = boardId;
    }

    return BoardItemsManager.instance?.insertNewBoardItem<ItemModel.PDFImages>(
      ItemModel.PDFImages,
      config,
      { renderInNearestFreeSpace: !position && renderInNearestFreeSpace },
    );
  }

  private insertPDFAsSeparateImages(
    conversionResponse: ConversionResponse,
    selectedIndices: number[],
    pdfName: string,
    boardId?: string,
    renderInNearestFreeSpace: boolean = true,
    position?: { top: number; left: number },
  ) {
    const { imagesSizes, numberOfPages, imagesToken, imagesPrefix } = conversionResponse;
    // Images sizes are returned in a compressed format from the backend, we need to decompress to find the size of each page
    const decompressedImagesSizes = this.pdfManagerService.decompressImagesSizes({
      imagesSizes,
      numberOfPages,
      imagesToken,
      imagesPrefix,
    });

    let totalHeight = 0;
    // The position of the first page is the position that will determine the position of the other pages (all below it)
    let firstItemLeft: number | undefined = position?.left;
    let firstItemTop: number | undefined = position?.top;
    let firstInsertedItem: { configurations?: { left?: number; top?: number } } | undefined;

    selectedIndices.forEach((i, index) => {
      const imageUrl = this.pdfManagerService.getImageUrl(i, imagesPrefix, imagesToken);

      const config: any = {
        url: imageUrl,
        // Reference back to the original uploaded PDF/office file in `pdf-uploads/`,
        // preserved even though the page is placed as a standalone image.
        sourceUrl: conversionResponse.sourceUrl,
        // To match previous rendering scale
        scaleX: RENDERED_PDF_PAGES_SCALE,
        scaleY: RENDERED_PDF_PAGES_SCALE,
        width: decompressedImagesSizes[i].width,
        height: decompressedImagesSizes[i].height,
        name: `${pdfName} - Page ${i + 1}`,
        locked: true,
      };

      if (boardId) {
        config.boardId = boardId;
      }

      let providedCreationContext: any = { renderInNearestFreeSpace: false };
      if (firstItemLeft !== undefined && firstItemTop !== undefined) {
        config.left = firstItemLeft;
        config.top = firstItemTop + totalHeight;
      } else {
        providedCreationContext = { renderInNearestFreeSpace };
      }

      const item = BoardItemsManager.instance?.insertNewBoardItem(
        ItemModel.Image,
        config,
        providedCreationContext,
      );

      if (!firstInsertedItem && item) {
        firstInsertedItem = item;
      }

      if (index === 0 && item) {
        firstItemLeft = item.configurations.left;
        firstItemTop = item.configurations.top;
      }

      totalHeight += decompressedImagesSizes[i].height;
    });
    return firstInsertedItem;
  }

  private createPageFrame(fileName: string, boardFolderUid?: string): Frame {
    return new Frame({
      name: fileName,
      backgroundPattern: this.spaceRepo.activeSpace?.sessionDefaultFramesBackground?.pattern,
      backgroundColor: this.spaceRepo.activeSpace?.sessionDefaultFramesBackground?.color,
      boardFolderUid,
    });
  }

  /**
   * Creates a semi-transparent "ghost" image of the PDF page that follows the user's mouse cursor.
   * This allows the user to choose the exact placement location on the board.
   *
   * @param imageUrl - The URL of the image to display as a ghost.
   * @param conversionResponse - Metadata about the converted PDF images (sizes, tokens, etc.).
   * @returns A promise that resolves to the {left, top} coordinates where the user clicked, or undefined if the operation fails.
   */
  private addGhostImageToFollowMouse(
    imageUrl: string,
    conversionResponse: ConversionResponse,
  ): Promise<{ left: number; top: number } | undefined> {
    const canvas = this.sharedDataService.fabricCanvas;
    if (!canvas) {
      return Promise.resolve(undefined);
    }

    return new Promise((resolve) => {
      // 1. Prepare image dimensions
      const { imagesSizes, numberOfPages, imagesToken, imagesPrefix } = conversionResponse;
      const decompressedImagesSizes = this.pdfManagerService.decompressImagesSizes({
        imagesSizes,
        numberOfPages,
        imagesToken,
        imagesPrefix,
      });

      // Run outside Angular zone to avoid triggering change detection on every mouse move
      this.ngZone.runOutsideAngular(() => {
        // 2. Show instruction snackbar
        if (this.snackBar._openedSnackBarRef) {
          this.snackBar.dismiss();
          this.snackBar._openedSnackBarRef.afterDismissed();
        }
        this.snackBar.openFromComponent(PlaceFormulaSnackbarComponent, {
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: 'lock-snack-bar',
          data: this.translateService.instant('Click on the board to place your document'),
        });

        // 3. Load and configure the ghost image
        fabric.Image.fromURL(imageUrl, (img) => {
          // Calculate scale to match the final rendered size of the actual PDF page.
          // This scaling is used to match what setScaleBasedOnContent does in board-image.ts and also apply the final scale we apply for all PDF pages.
          const firstPageSize = decompressedImagesSizes[0];
          const scaleX = (firstPageSize.width / (img.width ?? 1)) * RENDERED_PDF_PAGES_SCALE;
          const scaleY = (firstPageSize.height / (img.height ?? 1)) * RENDERED_PDF_PAGES_SCALE;

          const position = this.sessionsVptService.getCenterCoords();

          img.set({
            scaleX,
            scaleY,
            opacity: 0.7, // Semi-transparent
            originX: 'center',
            originY: 'center',
            selectable: false, // User can't select/drag it normally
            evented: false,
            hasControls: false,
            hasBorders: false,
            excludeFromExport: true, // Don't include in board exports
            top: position.y,
            left: position.x,
          });

          // Custom property to prevent sync/remote addition
          (img as any).skipRemoteAddition = true;

          canvas.add(img);
          img.setCoords();
          canvas.renderAll();

          // 4. Setup Event Listeners

          // Follow mouse movement
          const moveSubscription = fromEvent<fabric.IEvent>(canvas, 'mouse:move').subscribe(
            (opt) => {
              const pointer = canvas.getPointer(opt.e);
              img.set({
                left: pointer.x,
                top: pointer.y,
              });
              img.setCoords();
              canvas.requestRenderAll();
            },
          );

          // Handle placement click
          const clickSubscription = fromEvent<fabric.IEvent>(canvas, 'mouse:down')
            .pipe(filter((opt) => (opt.e as MouseEvent).button === 0)) // Left click only
            .subscribe((opt) => {
              const pointer = canvas.getPointer(opt.e);

              // 5. Cleanup
              canvas.remove(img);
              canvas.requestRenderAll();
              moveSubscription.unsubscribe();
              clickSubscription.unsubscribe();

              this.snackBar.dismiss();
              this.snackBar?._openedSnackBarRef?.afterDismissed();

              // Calculate top-left position from center pointer
              const scaledWidth = (img.width || 0) * (img.scaleX || 1);
              const scaledHeight = (img.height || 0) * (img.scaleY || 1);
              resolve({
                left: pointer.x - scaledWidth / 2,
                top: pointer.y - scaledHeight / 2,
              });
            });

          // Auto zoom-out to 75% if currently zoomed in above that level.
          // Uses instant zoom instead of animateZoomTo() for performance — animated zoom
          // triggers ~18 re-renders over 300ms, which is expensive on large boards (6000+ objects).
          const PDF_PLACEMENT_ZOOM_THRESHOLD = 0.75;
          const currentZoom = canvas.getZoom();
          if (currentZoom > PDF_PLACEMENT_ZOOM_THRESHOLD) {
            const centerPoint = new fabric.Point(
              canvas.getCenter().left as number,
              canvas.getCenter().top as number,
            );
            this.sessionsVptService.zoomToPoint(centerPoint, PDF_PLACEMENT_ZOOM_THRESHOLD);
          }
        });
      });
    });
  }

  public async bulkFlowInsertPdfItems(options: BulkInsertOptions) {
    if (!options.items.length) {
      return;
    }

    const arrangement = options.arrangement ?? PdfArrangement.Columns;
    const renderInNearestFreeSpace = options.renderInNearestFreeSpace ?? true;
    const uploadAsSeparateImages = options.uploadAsSeparateImages ?? false;

    if (options.strategy === PDFBoardStrategy.CurrentBoard && options.items.length === 1) {
      // This enables users to choose where to put the PDF given it is only one PDF in the current board
      this.getImagesAndSubmit(
        options.items[0].contentId,
        PDFBoardStrategy.CurrentBoard,
        options.items[0].selectedIndices,
        options.items[0].pdfName,
        uploadAsSeparateImages,
      );
      return;
    }

    if (options.strategy === PDFBoardStrategy.OneBoardPerPage && options.items.length === 1) {
      await this.insertOnePagePerBoard(options.items[0], uploadAsSeparateImages);
      return;
    }

    if (options.strategy === PDFBoardStrategy.OneBoardPerPage && options.items.length > 1) {
      await this.insertOneFilePerBoard(options.items, uploadAsSeparateImages);
      return;
    }

    const boardId = this.resolveTargetBoardId(options);
    if (!boardId) {
      return;
    }

    await this.insertPdfsWithArrangement({
      items: options.items,
      arrangement,
      boardId,
      renderInNearestFreeSpace,
      uploadAsSeparateImages,
    });
  }

  private async insertOnePagePerBoard(item: BulkPdfInsertItem, uploadAsSeparateImages: boolean) {
    const conversionResponse = await this.pdfManagerService.getConvertedImages(item.contentId);
    if (!conversionResponse) {
      return;
    }
    this.insertPDFsInBoardFolder(
      item.contentId,
      conversionResponse,
      item.selectedIndices,
      item.pdfName,
      uploadAsSeparateImages,
    );
  }

  private async insertOneFilePerBoard(items: BulkPdfInsertItem[], uploadAsSeparateImages: boolean) {
    if (!this.spaceRepo.activeSpace || !this.spaceRepo.activeSpace.currentRoomUid) {
      return;
    }

    // bulk creation of boards for each file
    const frameEntries = items
      .map((item) => ({ item, frame: this.createPageFrame(item.pdfName) }))
      .filter(
        (entry): entry is { item: BulkPdfInsertItem; frame: Frame } => entry.frame !== undefined,
      );
    if (!frameEntries.length) {
      return;
    }
    this.realtimeSpaceService.service.addFrames(
      frameEntries.map((entry) => entry.frame),
      this.spaceRepo.activeSpace._id,
      this.spaceRepo.activeSpace.currentRoomUid,
    );
    this.sharedDataService.selectFrameSubject.next(frameEntries[0].frame.uid);

    // So the file will be inserted in the center
    this.sessionsVptService.resetCanvasToOrigin();

    await Promise.all(
      frameEntries.map(async ({ item, frame }) => {
        const conversionResponse = await this.pdfManagerService.getConvertedImages(item.contentId);
        if (!conversionResponse) {
          return;
        }
        this.insertPDFItem(
          item.contentId,
          conversionResponse,
          item.selectedIndices,
          item.pdfName,
          frame.uid,
          true,
          undefined,
          uploadAsSeparateImages,
        );
      }),
    );
  }

  private createAndSelectBoard(name: string) {
    const board = this.createPageFrame(name);
    if (!board || !this.spaceRepo.activeSpace || !this.spaceRepo.activeSpace.currentRoomUid) {
      return;
    }
    this.realtimeSpaceService.service.addFrame(
      board,
      this.spaceRepo.activeSpace._id,
      this.spaceRepo.activeSpace.currentRoomUid,
    );
    this.sharedDataService.selectFrameSubject.next(board.uid);

    // So the file will be inserted in the center
    this.sessionsVptService.resetCanvasToOrigin();
    return board;
  }

  private resolveTargetBoardId(options: BulkInsertOptions) {
    if (options.strategy === PDFBoardStrategy.NewBoard) {
      const board = this.createAndSelectBoard(
        options.items[0]?.pdfName ?? this.translateService.instant('New board'),
      );
      return board?.uid;
    }
    return options.boardId ?? this.spaceBoardsService.activeSpaceSelectedBoard?.uid ?? undefined;
  }

  private async insertPdfsWithArrangement(options: {
    items: BulkPdfInsertItem[];
    arrangement: PdfArrangement;
    boardId?: string;
    renderInNearestFreeSpace?: boolean;
    uploadAsSeparateImages?: boolean;
  }) {
    if (!options.items.length) {
      return;
    }

    const itemsWithPdfConversionResponse = await Promise.all(
      options.items.map(async (item) => {
        const conversionResponse = await this.pdfManagerService.getConvertedImages(item.contentId);
        if (!conversionResponse) {
          return undefined;
        }
        return { ...item, conversionResponse };
      }),
    );

    const validItems = itemsWithPdfConversionResponse.filter(
      (item): item is BulkPdfInsertItem & { conversionResponse: ConversionResponse } => !!item,
    );
    if (!validItems.length) {
      return;
    }

    const pdfsSizes = validItems.map((item) =>
      this.getPdfRenderedSize(item.conversionResponse, item.selectedIndices),
    );
    const arrangement = options.arrangement ?? PdfArrangement.Stacked;
    const firstItem = validItems[0];

    // We render the first in the nearest free space, and then we render the rest based on its position
    const firstInserted = this.insertPDFItem(
      firstItem.contentId,
      firstItem.conversionResponse,
      firstItem.selectedIndices,
      firstItem.pdfName,
      options.boardId,
      options.renderInNearestFreeSpace ?? true,
      undefined,
      options.uploadAsSeparateImages ?? false,
    );

    const baseLeft =
      (firstInserted as any)?.configurations?.left ?? this.sessionsVptService.getCenterCoords().x;
    const baseTop =
      (firstInserted as any)?.configurations?.top ?? this.sessionsVptService.getCenterCoords().y;

    // Calculating where to insert each PDF
    const layout = this.getPDFsOffsets(pdfsSizes, arrangement, this.GAP_BETWEEN_PDFS);

    validItems.forEach((item, index) => {
      if (index === 0) {
        // as the first item was already inserted
        return;
      }
      const position = {
        left: baseLeft + layout.leftOffsets[index],
        top: baseTop + layout.topOffsets[index],
      };

      this.insertPDFItem(
        item.contentId,
        item.conversionResponse,
        item.selectedIndices,
        item.pdfName,
        options.boardId,
        false,
        position,
        options.uploadAsSeparateImages ?? false,
      );
    });

    if (validItems.length > 1 && firstInserted) {
      this.focusOnInsertedItem(firstInserted);
    }
  }

  private getPdfRenderedSize(
    conversionResponse: ConversionResponse,
    selectedIndices: number[],
  ): { width: number; height: number } {
    const { imagesSizes, numberOfPages, imagesToken, imagesPrefix } = conversionResponse;
    const decompressedImagesSizes = this.pdfManagerService.decompressImagesSizes({
      imagesSizes,
      numberOfPages,
      imagesToken,
      imagesPrefix,
    });
    const fallback = { width: 0, height: 0 };
    if (!decompressedImagesSizes.length) {
      return fallback;
    }
    const sizes = selectedIndices.length ? selectedIndices : [0];
    const width = Math.max(...sizes.map((index) => decompressedImagesSizes[index]?.width ?? 0));
    const height = sizes.reduce(
      (sum, index) => sum + (decompressedImagesSizes[index]?.height ?? 0),
      0,
    );
    return {
      width,
      height,
    };
  }

  private getPDFsOffsets(
    sizes: { width: number; height: number }[],
    arrangement: PdfArrangement,
    gap: number,
  ) {
    const leftOffsets: number[] = [];
    const topOffsets: number[] = [];
    let totalLeft = 0;
    let totalTop = 0;

    sizes.forEach((size, index) => {
      if (index === 0) {
        leftOffsets.push(0);
        topOffsets.push(0);
        return;
      }

      if (arrangement === PdfArrangement.Columns) {
        totalLeft += sizes[index - 1].width + gap;
        leftOffsets.push(totalLeft);
        topOffsets.push(0);
        return;
      }

      totalTop += sizes[index - 1].height + gap;
      leftOffsets.push(0);
      topOffsets.push(totalTop);
    });

    return { leftOffsets, topOffsets };
  }

  private focusOnInsertedItem(item?: {
    configurations?: { height?: number; left?: number; top?: number };
  }) {
    const fabricCanvas = this.sharedDataService.fabricCanvas;
    if (!fabricCanvas || !item) {
      return;
    }
    // We need this because we need to wait for the inserted item to be properly rendered in fabric,
    // and there is no event that fabric emits when this happens
    modifiedSetTimeout(
      () => {
        this.sessionsVptService.makeCanvasFitItsContent(
          fabricCanvas,
          [item as any],
          undefined,
          undefined,
          true,
          item.configurations?.height,
        );
      },
      100,
      true,
    );
  }
}
